// Copying a week onto another week.
//
// Two steps, always. The first writes nothing and asks the server what would
// happen; only then is there a button that does it. A copy that silently
// dropped a shift, or silently put somebody onto their own approved vacation,
// is worse than one that refuses — and both are invisible until the morning
// nobody turns up.
//
// A blocked shift is not abandoned by default. Square's answer is to offer it
// as an open shift, which keeps the work on the board with the honest label
// "somebody needs to do this and it isn't them", and that is what the
// checkboxes here decide.

import { useState } from 'react';
import { timeRange } from '../../lib/shifts';
import { fmtDate } from '../../lib/dates';

function shortDay(iso) {
  return fmtDate(iso, 'dayShort');
}

export default function CopyWeekDialog({ from, to, preview, busy, error, onConfirm, onClose }) {
  // Every blocked shift starts ticked: the work still needs doing, and the
  // manager is more likely to want it visible and unassigned than gone.
  const [asOpen, setAsOpen] = useState(() => new Set((preview?.blocked || []).map((b) => b.sourceId)));

  const blocked = preview?.blocked || [];
  const willCopy = preview?.willCopy ?? 0;
  const allTicked = blocked.length > 0 && blocked.every((b) => asOpen.has(b.sourceId));

  function toggle(id) {
    setAsOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-xl rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Copy week"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Copy this week forward</h2>
            <p className="text-sm text-ink-500">
              {shortDay(from)} → {shortDay(to)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {!preview ? (
            <p className="text-sm text-ink-400">Working out what would happen…</p>
          ) : (
            <>
              <p className="text-sm text-ink-700">
                {willCopy === 0 && blocked.length === 0 ? (
                  'There is nothing in this week to copy.'
                ) : (
                  <>
                    <strong>
                      {willCopy} shift{willCopy === 1 ? '' : 's'}
                    </strong>{' '}
                    will be copied to the week of {shortDay(to)}. They arrive as a draft — nobody is told
                    anything until you publish.
                  </>
                )}
              </p>

              {blocked.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-amber-200 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-medium text-amber-900">
                        {blocked.length} shift{blocked.length === 1 ? '' : 's'} cannot be copied
                      </h3>
                      <p className="text-xs text-amber-800 mt-0.5">
                        Bring them across unassigned so the work stays on the board, or leave them out.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-amber-900 shrink-0 pt-0.5">
                      <input
                        type="checkbox"
                        checked={allTicked}
                        onChange={(e) =>
                          setAsOpen(e.target.checked ? new Set(blocked.map((b) => b.sourceId)) : new Set())
                        }
                      />
                      All
                    </label>
                  </div>
                  <ul className="divide-y divide-amber-200">
                    {blocked.map((b) => (
                      <li key={b.sourceId} className="px-4 py-3 flex items-start gap-3">
                        <input
                          type="checkbox"
                          id={`open-${b.sourceId}`}
                          className="mt-1"
                          checked={asOpen.has(b.sourceId)}
                          onChange={() => toggle(b.sourceId)}
                        />
                        <label htmlFor={`open-${b.sourceId}`} className="min-w-0 flex-1 cursor-pointer">
                          <span className="block text-sm text-ink-800">
                            {shortDay(b.date)} · {timeRange(b.startTime, b.endTime)}
                            {b.name ? ` · ${b.name}` : ''}
                          </span>
                          {/* The reason, not "conflict". A manager can act on
                              "Time off — all day · Away at a show"; they cannot act on
                              a word that only says something is wrong. */}
                          <span className="block text-xs text-amber-800 mt-0.5">{b.reason}</span>
                        </label>
                        <span className="text-xs text-ink-400 shrink-0 pt-0.5">
                          {asOpen.has(b.sourceId) ? 'Open shift' : 'Skipped'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {error && <p className="text-sm text-rose-600">{error}</p>}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !preview || (willCopy === 0 && asOpen.size === 0)}
            onClick={() => onConfirm([...asOpen])}
          >
            {busy ? 'Copying…' : `Copy ${willCopy + asOpen.size} shift${willCopy + asOpen.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
